import { useCallback } from 'react';

import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import { ConnectionChangeType } from 'features/call/call-base/enums';
import { setDeafIsLoading } from 'features/call/call-deaf/store';
import { ParticipantsStatusEventBus } from 'features/call/call-status/services';
import {
  SignMailRecordingStatus,
  SignMailStatus,
} from 'features/signmail/enums';
import { SignMailEventBus } from 'features/signmail/services';
import {
  changeSignMailStatus,
  selectSignMailRecordingStatus,
  startSignMailCountdown,
  startSkippedSignMailSession,
  endSignMailSessionWithShowLocalVideo,
} from 'features/signmail/store';
import { sendAnalyticsInfo } from 'features/analytics/helpers';
import { useCallDeafParticipantHandlers } from 'features/call/call-accepted/hooks';

export const useSignMailHandlers = () => {
  const dispatch = useAppDispatch();
  const signMailRecordingStatus = useAppSelector(selectSignMailRecordingStatus);
  const { handleDeafCancel } = useCallDeafParticipantHandlers();

  const handleEndSignMailSession = useCallback(() => {
    dispatch(setDeafIsLoading(true));
    if (signMailRecordingStatus === SignMailRecordingStatus.RECORDING) {
      SignMailEventBus.recordingFinished$.next();
    } else {
      ParticipantsStatusEventBus.deaf.$disconnected.next({
        connectionChangeType: ConnectionChangeType.AGENT_DISCONNECT_DEAF,
      });
    }
    ParticipantsStatusEventBus.deaf.$disconnectSignMail.next();
    dispatch(endSignMailSessionWithShowLocalVideo());
  }, [dispatch, signMailRecordingStatus]);

  const handleCountdownEnded = () => {
    dispatch(changeSignMailStatus(SignMailStatus.RECORDING));
  };

  const handleSkipToSignMail = useCallback(() => {
    handleDeafCancel();
    const didStart = dispatch(startSkippedSignMailSession());
    if (!didStart) {
      dispatch(
        sendAnalyticsInfo({
          Method: 'handleSkipToSignMail',
          message:
            'Cant start new signmail session. AST owns SignMail or a session is already in progress.',
          LogGroup: 'SignMail',
        })
      );
      return;
    }
    dispatch(
      sendAnalyticsInfo({
        Method: 'handleSkipToSignMail',
        message:
          'Agent skipped to signmail using skip button. Signmail session started.',
        LogGroup: 'SignMail',
      })
    );
  }, [dispatch, handleDeafCancel]);
  const handleGreetingEnded = useCallback(() => {
    dispatch(startSignMailCountdown());
  }, [dispatch]);

  return {
    handleEndSignMailSession,
    handleGreetingEnded,
    handleCountdownEnded,
    handleSkipToSignMail,
  };
};
